import React from "react";
import { FiRefreshCw, FiSearch, FiUserCheck, FiBarChart2, FiLayers, FiZap } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const usabilityHeroData = {
  backgroundImage: "/images/serviceBanners/useabilitiesTesting.webp",
  whiteImage: "/images/serviceBanners/usababilityW.webp",
  subTitle: "Creating User-Centered Digital Experiences Through Usability Testing",
  title: (
    <>
      Usability Testing & <br /> <span className="text-purple-600">Iteration Solutions</span>
    </>
  ),
  description:
    "We improve digital experiences through structured usability testing, user behavior analysis, and iterative design refinements to enhance product performance, increase user satisfaction, and ensure seamless and effective user journeys.",
  primaryButton: {
    title: "Request a UX Audit",
    onClick: () => console.log("Audit clicked"),
  },
  secondaryButton: {
    title: "View Case Studies",
    onClick: () => console.log("Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "User-Centered Experience Optimization",
      desc: "Improve usability through structured testing and real user feedback.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Iterative Design Improvements",
      desc: "Refine interfaces continuously based on user behavior insights.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Performance & Usability Testing",
      desc: "Ensure smooth, fast, and error-free digital experiences.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Experience Validation Systems",
      desc: "Validate design decisions through real-world user interaction data.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block
export const usabilityStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const usabilitySolutionsAndProcessData = {
  buildSubtitle: "OUR TESTING MODALITIES",
  buildTitle: "Usability Testing & Iteration Solutions for Better User Experience",
  buildCards: [
    {
      title: "Cross-Platform App Development",
      desc: "Build fast mobile apps for Android and iOS using scalable cross-platform technology.",
      icon: FiSearch,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise Mobile Solutions",
      desc: "Secure and scalable apps designed for enterprise performance and efficiency.",
      icon: FiLayers,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce App Development",
      desc: "Feature-rich shopping apps with smooth UX and secure payments.",
      icon: FiZap,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand App Solutions",
      desc: "Real-time booking and delivery apps with reliable performance.",
      icon: FiBarChart2,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech App Development",
      desc: "Secure financial apps with modern features and strong data protection.",
      icon: FiLayers,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom Mobile App Solutions",
      desc: "Tailored apps built to match specific business needs and goals.",
      icon: FiSearch,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR METHODOLOGY",
  processTitle: "Usability Testing & Iteration Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Research",
      description: "Understand user behavior, business goals, and identify usability issues.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define UX goals, testing approach, and improvement roadmap.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Design Validation",
      description: "Test UI/UX designs early to ensure better usability before development.",
      icon: LuRocket,
    },
    {
      step: "04",
      title: "Usability Testing",
      description: "Analyze real user interactions to identify friction points.",
      icon: LuUsers,
    },
    {
      step: "05",
      title: "Iteration & Optimization",
      description: "Improve product experience continuously based on feedback.",
      icon: LuCode,
    },
    {
      step: "06",
      title: "Deployment & Monitoring",
      description: "Release updates and monitor user behavior for ongoing improvements.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR USABILITY TESTING & ITERATION SERVICES?",
  benefitsTitle: "Why Businesses Choose Usability Testing & Iteration Solutions",
  benefits: [
    "Cross-platform support",
    "Faster development speed",
    "Modern user interface",
    "Smooth app performance",
    "Efficient workflow process",
    "Scalable mobile solutions",
    "Cost-effective development",
  ],
  logoImage: "/images/serviceBanners/useabilitiesTesting.webp",
  whiteLogoImage: "/images/serviceBanners/usababilityW.webp",
};

// 4. Portfolio Data
export const usabilityPortfolioData = {
  subtitle: "OUR CASE STUDIES",
  title: "Iteration That Drove Impact",
  buttonText: "View All Projects",
  buttonLink: "#",
  apps: [
    {
      title: "FinTech Dashboard",
      badge: "Usability Sprint",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Eliminating 40% of friction points in a complex transactional flow.",
      downloads: "+15% Completion",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop",
    },
  ],
};

// 5. Technologies Data
export const usabilityTechnologiesData = {
  techTitle: "OUR TESTING TOOL STACK",
  technologies: [
    { name: "Postman", icon: "RestAPI" },
    { name: "Cypress", icon: "Vite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Hotjar", icon: "Provider" },
    { name: "Jira", icon: "CLI" },
    { name: "NewRelic", icon: "SQLite" },
  ],
  trustTitle: "TRUSTED BY QA TEAMS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const usabilityAboutData = [
  {
    title: "Usability Testing Services for Better Digital Performance",
    content: (
      <>
        Digital products often struggle when businesses assume what users want instead of understanding how they actually behave. Effective{" "}
        <b className="text-text-primary font-semibold">usability testing services</b> help organizations identify friction points, confusing interactions, and usability gaps that
        affect engagement and customer satisfaction. Businesses that prioritize testing before scaling often reduce costly product mistakes and improve user confidence. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, testing strategies are designed to uncover practical insights that help businesses create smoother
        and more intuitive digital experiences.
      </>
    ),
  },
  {
    title: "User Experience Testing Company for Smarter Product Decisions",
    content: (
      <>
        Strong digital products are built through continuous learning and improvement. A reliable <b className="text-text-primary font-semibold">user experience testing company</b>{" "}
        helps businesses understand why users abandon flows, struggle with navigation, or fail to complete key actions. Companies investing in{" "}
        <b className="text-text-primary font-semibold">ui ux testing services</b> often improve product performance because design decisions are validated through real-world
        feedback instead of assumptions. Tycho Technologies focuses on practical testing methods that help businesses improve functionality while supporting long-term product
        success.
      </>
    ),
  },
  {
    title: "Product Usability Research That Improves User Retention",
    content: (
      <>
        User retention becomes stronger when products feel simple, intuitive, and easy to navigate. Effective{" "}
        <b className="text-text-primary font-semibold">product usability research</b> helps businesses identify behavioral patterns and usability challenges before they impact
        growth. Organizations looking to strengthen digital product experiences often combine testing efforts with{" "}
        <a href="https://tychotechnologies.com/web-SaaS-dashboard-design-india" className="text-cyan-400 underline hover:text-cyan-300">
          saas dashboard design services
        </a>{" "}
        to improve usability across platforms and support better decision-making through smoother user interactions.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Usability Testing Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">usability testing company in india</b> continues to grow as businesses seek measurable ways to
        improve product performance and customer experience. Companies investing in <b className="text-text-primary font-semibold">ux testing services india</b> often prioritize
        better engagement, reduced usability friction, and long-term customer satisfaction. Businesses working with Tycho Technologies benefit from customized testing frameworks
        designed around real user behavior and business objectives. Many organizations also strengthen product experiences through{" "}
        <a href="https://tychotechnologies.com/mobile-app-design-india" className="text-cyan-400 underline hover:text-cyan-300">
          mobile app design services
        </a>{" "}
        to create consistent and user-friendly digital solutions.
      </>
    ),
  },
];
// 7. FAQs (Preserved)
export const usabilityFaqs = [
  {
    q: "When should we start usability testing?",
    a: "As early as possible! Testing wireframes saves significant development costs compared to testing a finished product.",
  },
  {
    q: "How many users do you typically test with?",
    a: "According to industry standards, testing with 5 users typically identifies 85% of usability issues.",
  },
];
